import {TreatmentLine, TreatmentLineDosageDraft} from "@/medication-checkup/interfaces/treatment-selections";
import TreatmentLineDosageService from "@/medication-checkup/services/TreatmentLineDosageService";
import DosageValidationService from "@/dosage/services/DosageValidationService";

/**
 * The posology a line stands with, as the review shows it and the closing
 * action writes it.
 */
export interface LinePosology {
    /**
     * The draft the line carries: the one the pharmacist saved, else the
     * proposal read on its prescription when the form would take it — null
     * when the line still asks for its posology.
     */
    draft: TreatmentLineDosageDraft | null;
    /**
     * The posology in words, null with no draft.
     */
    text: string | null;
    /**
     * Whether the pharmacist saved something other than what was read.
     */
    edited: boolean;
}

/**
 * Whether two drafts write the same posology: compared on what the closing
 * action carries, so a form reopened and saved untouched is no edit.
 */
const same = (line: TreatmentLine, first: TreatmentLineDosageDraft, second: TreatmentLineDosageDraft): boolean => {
    return JSON.stringify(TreatmentLineDosageService.toDosage(line.product_id, first))
        === JSON.stringify(TreatmentLineDosageService.toDosage(line.product_id, second));
};

const LinePosologies = () => {
    return {
        /**
         * The proposal of a line as a draft, when the form would take it as
         * is. One it refuses — a dose missing, a take empty — is no
         * posology: the line asks for it, and the dialog shows what fails.
         */
        proposed(line: TreatmentLine): TreatmentLineDosageDraft | null {
            if (!line.dosage_proposal) {
                return null;
            }

            const draft = TreatmentLineDosageService.proposalDraft(line.dosage_proposal);

            return DosageValidationService.validate(draft.data, line.product_id, false).isValid ? draft : null;
        },

        /**
         * The posology of a line under review: the draft the pharmacist
         * saved, else the proposal read on the prescription — shown, the
         * proposal is the posology unless the pharmacist changes it.
         */
        live(line: TreatmentLine, saved: TreatmentLineDosageDraft | null): LinePosology {
            const proposed = this.proposed(line);
            const draft = saved ?? proposed;

            return {
                draft,
                text: draft ? TreatmentLineDosageService.describe(draft.data, draft.comment) : null,
                edited: saved !== null && proposed !== null && !same(line, saved, proposed)
            };
        },

        /**
         * The posology the record holds for a line, once the review is over.
         */
        recorded(line: TreatmentLine): LinePosology {
            if (!line.dosage) {
                return {draft: null, text: null, edited: false};
            }

            const draft = TreatmentLineDosageService.draftOf(line);

            return {
                draft,
                text: TreatmentLineDosageService.describe(draft.data, draft.comment),
                edited: line.dosage_provenance === 'edited'
            };
        }
    };
};

export default LinePosologies();
